
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const FAQPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const categories = ['All', 'Course', 'Licensing', 'Technical', 'Billing', 'General'];

  const faqs = [
    {
      category: 'Course',
      question: "How long do I have access to the course?",
      answer: "You get lifetime access to the course materials. Once you purchase, you can access the content anytime, anywhere, and review it as many times as needed. There are no time limits or expiration dates."
    },
    {
      category: 'Licensing',
      question: "How long does it take to get my Georgia dealer license?",
      answer: "The typical timeline is 60-90 days from start to finish. This includes completing our course (1-2 weeks), gathering required documents (2-3 weeks), finding and securing a location (2-4 weeks), and going through the state approval process (4-6 weeks)."
    },
    {
      category: 'Licensing',
      question: "What's the total cost to get my dealer license?",
      answer: "The total investment is typically $3,000-$5,000, which includes our course ($97), state application fees ($300), surety bond ($300-500), insurance ($800-1,200), location setup, signage, and other required expenses. Our course provides a detailed budget breakdown."
    },
    {
      category: 'Licensing',
      question: "Do I need a physical location before applying?",
      answer: "Yes, you must have a physical business location secured before submitting your application to the Georgia Department of Revenue. The location must meet specific zoning and space requirements, which our course covers in detail."
    },
    {
      category: 'Technical',
      question: "Can I download the course materials?",
      answer: "Some materials like templates, checklists, and guides are downloadable as PDFs. However, the main video content is streamed through our secure Teachable platform to protect intellectual property and ensure you always have the most up-to-date information."
    },
    {
      category: 'Licensing',
      question: "Do I still need to attend the GIADA seminar?",
      answer: "Yes, the Georgia Independent Automobile Dealers Association (GIADA) seminar is still required by the state for all new dealer license applicants. However, our course prepares you thoroughly so you'll understand everything covered in the seminar and can focus on networking."
    },
    {
      category: 'Course',
      question: "How is this different from free information online?",
      answer: "Our course provides the complete, step-by-step system with current 2024 requirements, insider tips, and proven strategies from someone who has successfully helped 500+ people get licensed. Free information is often outdated, incomplete, or incorrect, leading to costly mistakes and delays."
    },
    {
      category: 'Course',
      question: "How many modules are in the course?",
      answer: "The course contains 10 comprehensive modules covering every aspect of getting your Georgia dealer license, from initial planning and legal requirements to final approval and growing your business."
    },
    {
      category: 'Technical',
      question: "How do I access the course after purchase?",
      answer: "After purchase, you'll receive an email with login instructions for our Teachable platform. You can access the course at https://michael-jackson-s-school7.teachable.com using the account you created during checkout."
    },
    {
      category: 'Course',
      question: "Can I take the course on my phone or tablet?",
      answer: "Yes! The course is fully mobile-friendly and works on all devices. You can watch videos, download materials, and track your progress from your phone, tablet, or computer."
    },
    {
      category: 'Licensing',
      question: "What if my application gets denied?",
      answer: "With our proven system and guidance, we have a 95% success rate. If you follow our course exactly and your application is still denied for reasons within our control, we'll work with you to address the issues and reapply."
    },
    {
      category: 'General',
      question: "Do you offer refunds?",
      answer: "Due to the digital nature of the course and immediate access to valuable content, all sales are final. However, we're confident in our course quality and offer extensive support to ensure your success."
    },
    {
      category: 'General',
      question: "How can I contact support?",
      answer: "You can reach our support team at Info@georgiadealerlicense.com. We typically respond within 24 hours during business days. For urgent questions, you can also book a consulting call."
    },
    {
      category: 'Licensing',
      question: "Can I get a license if I have a criminal background?",
      answer: "Georgia has specific guidelines about criminal backgrounds. Minor offenses may not disqualify you, but felonies related to fraud, theft, or financial crimes typically will. Our course covers this topic and helps you understand your eligibility."
    },
    {
      category: 'Billing',
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards (Visa, MasterCard, American Express, Discover) through our secure Stripe payment system. Payment is processed immediately upon purchase."
    }
  ];

  const filteredFAQs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-teal-100">
              Everything you need to know about getting your Georgia Dealer License
            </p>
          </div>
        </section>

        {/* Search and Filter Section */}
        <section className="py-8 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row gap-4 mb-8">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    placeholder="Search questions..."
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              
              {/* Category Filter */}
              <div className="md:w-48">
                <select
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Results Count */}
            <p className="text-gray-600 mb-6">
              Showing {filteredFAQs.length} of {faqs.length} questions
            </p>
          </div>
        </section>

        {/* FAQ Content */}
        <section className="py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {filteredFAQs.length === 0 ? (
              <div className="text-center py-12">
                <i className="ri-search-line text-4xl text-gray-400 mb-4"></i>
                <h3 className="text-xl font-medium text-gray-900 mb-2">No questions found</h3>
                <p className="text-gray-600">Try adjusting your search terms or category filter.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredFAQs.map((faq, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <button
                      className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors"
                      onClick={() => toggleFAQ(index)}
                    >
                      <div className="flex-1 pr-4">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-teal-600 bg-teal-50 px-2 py-1 rounded">
                            {faq.category}
                          </span>
                        </div>
                        <span className="font-medium text-gray-900">{faq.question}</span>
                      </div>
                      <span className="flex-shrink-0">
                        {openIndex === index ? (
                          <i className="ri-subtract-line text-xl text-teal-600"></i>
                        ) : (
                          <i className="ri-add-line text-xl text-teal-600"></i>
                        )}
                      </span>
                    </button>
                    {openIndex === index && (
                      <div className="px-6 pb-4">
                        <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Still Have Questions?
            </h2>
            <p className="text-gray-600 mb-8">
              Can't find what you're looking for? Our team is here to help you succeed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/consulting"
                className="inline-flex items-center justify-center px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-chat-3-line mr-2"></i>
                Book Consulting Call
              </Link>
              <a
                href="mailto:Info@georgiadealerlicense.com"
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-teal-600 font-medium rounded-lg border-2 border-teal-600 hover:bg-teal-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-mail-line mr-2"></i>
                Email Support
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FAQPage;
